/**
 * Dashboard widget — "Contracts awaiting OUR counter-signature"
 * Lists contract packs the customer has already signed that still need Helios
 * to counter-sign. Highlights anything sitting for more than 2 days.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { PenLine, Loader2, ChevronRight, Clock } from 'lucide-react';
import api from '../lib/api';

export const ContractsAwaitingCountersignWidget = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/contract-packs/awaiting-countersign')
      .then(({ data }) => setRows(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card data-testid="awaiting-countersign-loading">
        <CardContent className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></CardContent>
      </Card>
    );
  }
  if (!rows.length) return null;

  const stale = rows.filter((r) => (r.days_since_signed ?? 0) >= 2);

  return (
    <Card data-testid="awaiting-countersign-widget" className="border-blue-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <PenLine className="w-5 h-5 text-blue-600" />
          Contracts Awaiting Our Signature
          <Badge variant="outline" className="ml-2 border-blue-300 text-blue-700 text-[10px]">
            {rows.length} to counter-sign
          </Badge>
          {stale.length > 0 && (
            <Badge className="bg-rose-100 text-rose-700 border border-rose-200 text-[10px]">
              {stale.length} waiting &gt; 2 days
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {rows.map((r) => {
          const isStale = (r.days_since_signed ?? 0) >= 2;
          return (
            <div
              key={r.pack_id}
              className={`flex flex-wrap items-center justify-between gap-2 p-2.5 rounded border ${
                isStale ? 'border-rose-200 bg-rose-50/40' : 'border-slate-100 hover:bg-slate-50'
              }`}
              data-testid={`countersign-row-${r.pack_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/customers/${r.customer_id}?tab=contracts`}
                    className="font-medium text-sm text-slate-800 hover:text-blue-700"
                  >
                    {r.customer_name || 'Unnamed'}
                  </Link>
                  <Badge variant="outline" className="text-[10px]">{r.pack_type?.replace('_', ' ')}</Badge>
                  <Badge className={`text-[10px] ${isStale ? 'bg-rose-100 text-rose-700 border border-rose-200' : 'border-blue-200 text-blue-700'}`} variant={isStale ? undefined : 'outline'}>
                    <Clock className="w-2.5 h-2.5 mr-1" />
                    Customer signed {r.days_since_signed ?? 0}d ago
                  </Badge>
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Signed {r.customer_signed_at ? new Date(r.customer_signed_at).toLocaleDateString('en-GB') : '—'}
                  {r.customer_email ? ` · ${r.customer_email}` : ''}
                </p>
              </div>
              <Link to={`/customers/${r.customer_id}?tab=contracts`}>
                <Button
                  size="sm"
                  variant={isStale ? 'default' : 'outline'}
                  className={`h-8 text-xs ${isStale ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
                  data-testid={`countersign-open-${r.pack_id}`}
                >
                  Counter-sign <ChevronRight className="w-3 h-3 ml-0.5" />
                </Button>
              </Link>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default ContractsAwaitingCountersignWidget;
